import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  CircularProgress,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Paper,
  MenuItem,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  Snackbar,
  Alert,
  Pagination
} from '@mui/material';
import {
  Search as SearchIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,

} from '@mui/icons-material';
import { supabase } from '../supabaseClient';
import { formatCurrency } from '../utils/formatters';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CreditCardItem from '../components/creditCards/CreditCardItem';
import CreditCardsSummary from '../components/creditCards/CreditCardsSummary';
import GuidedTour from '../components/tour/GuidedTour';
import TourButton from '../components/tour/TourButton';
import useTour from '../components/tour/useTour';
import { TOURS } from '../components/tour/tours';
import AmountField from '../components/AmountField';

// Helper function to format dates as DD/MM/YYYY
const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '-';

  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();

  return `${day}/${month}/${year}`;
};

// Filter values that can be opened from a link, e.g. /credit-cards?filter=unpaid (Dashboard uses these)
const FILTER_OPTIONS = [
  { value: 'all', label: 'All Cards' },
  { value: 'used', label: 'Used Cards' },
  { value: 'unused', label: 'Unused Cards' },
  { value: 'bill_generated', label: 'Bill Generated' },
  { value: 'outstanding', label: 'Outstanding (Unpaid, Overdue, Partial)' },
  { value: 'unpaid_all', label: 'Unpaid (incl. Overdue)' },
  { value: 'unpaid', label: 'Unpaid Bills' },
  { value: 'overdue', label: 'Overdue Bills' },
  { value: 'partially_paid', label: 'Partially Paid' },
  { value: 'paid_all', label: 'Paid / Nothing Due' },
  { value: 'fully_paid', label: 'Fully Paid' },
  { value: 'no_payment_required', label: 'No Payment Required' },
];

// Statuses each grouped filter covers (matches the Dashboard's card statistics)
const STATUS_GROUPS = {
  bill_generated: ['fully_paid', 'partially_paid', 'unpaid', 'overdue', 'no_payment_required'],
  outstanding: ['unpaid', 'overdue', 'partially_paid'],
  unpaid_all: ['unpaid', 'overdue'],
  paid_all: ['fully_paid', 'no_payment_required'],
  unpaid: ['unpaid'],
  overdue: ['overdue'],
  partially_paid: ['partially_paid'],
  fully_paid: ['fully_paid'],
  no_payment_required: ['no_payment_required'],
};

const CreditCards = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [creditCards, setCreditCards] = useState([]);
  const [filteredCards, setFilteredCards] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState(() => {
    const fromUrl = searchParams.get('filter');
    return FILTER_OPTIONS.some(option => option.value === fromUrl) ? fromUrl : 'all';
  });
  const [sortBy, setSortBy] = useState('name'); // Default sort by name
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'
  const [selectedCard, setSelectedCard] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [error, setError] = useState(null);
  const [rewardsDialogOpen, setRewardsDialogOpen] = useState(false);
  const [selectedCardRewards, setSelectedCardRewards] = useState(null);
  const [rewardsPointsInput, setRewardsPointsInput] = useState('');
  const [rewardsHistory, setRewardsHistory] = useState([]);
  const [paymentStatusSnackbar, setPaymentStatusSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [archiveStatus, setArchiveStatus] = useState('active');
  const cardsTour = useTour('credit-cards', { ready: !loading && !error });

  // Add sort options
  const sortOptions = [
    { value: 'name', label: 'Name (A to Z)' },
    { value: 'credit_limit', label: 'Credit Limit' },
    { value: 'available_limit', label: 'Available Limit' },
    { value: 'bill_date', label: 'Bill Date' },
    { value: 'spend_waiver', label: 'Spend Waiver' },
    { value: 'fee_waiver_progress', label: 'Fee Waiver Progress' },
    { value: 'anniversary_month', label: 'Anniversary Month' },
    { value: 'annual_fee', label: 'Annual Fee' }
  ];

  const months = React.useMemo(() => [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' }
  ], []);

  const getMonthName = React.useCallback((monthNumber) => {
    const month = months.find(m => m.value === monthNumber?.padStart(2, '0'));
    return month ? month.label : '-';
  }, [months]);

  // style for  number input
  const numberInputStyle = {
    '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
      '-webkit-appearance': 'none',
      margin: 0,
    },
    '& input[type=number]': {
      '-moz-appearance': 'textfield',
    },
  };

  // Add archive status options
  const archiveStatusOptions = [
    { value: 'active', label: 'Active Cards' },
    { value: 'archived', label: 'Archived Cards' },
    { value: 'all', label: 'All Cards' }
  ];

  const fetchCreditCards = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) {
        console.error('User authentication error:', userError);
        throw new Error(`Authentication error: ${userError.message}`);
      }
      if (!user) {
        console.error('No user found in session');
        throw new Error('Please sign in to view your credit cards');
      }

      console.log('Fetching cards for user:', user.id);

      // Fetch wallets with credit card details and transactions
      const { data, error } = await supabase
        .from('wallets')
        .select(`
          id,
          name,
          type,
          balance,
          credit_limit,
          user_id,
          account_number,
          card_variant,
          is_primary,
          primary_card_id,
          is_archived,
          credit_card_details (
            id,
            wallet_id,
            billing_date,
            due_date,
            anniversary_date,
            annual_fee,
            spend_waiver,
            rewards,
            rewards_value,
            payment_date,
            initial_usage
          ),
          transactions (
            id,
            amount,
            date,
            description,
            type
          )
        `)
        .eq('user_id', user.id)
        .eq('type', 'credit');

      if (error) {
        console.error('Database query error:', error);
        throw new Error(`Database error: ${error.message}`);
      }

      if (!data) {
        console.error('No data returned from database');
        throw new Error('No credit cards found in your account');
      }

      console.log('Successfully fetched cards:', data.length);

      // Process the data
      const processedCards = data.map(card => {
        const details = card.credit_card_details[0] || {};
        const transactions = card.transactions || [];
        console.log('Processing card:', card.name, details);

        // Get the primary card (either self or linked primary)
        const primaryCard = card.is_primary
          ? card
          : data.find(w => w.id === card.primary_card_id);

        if (!primaryCard) {
          console.warn('No primary card found for:', card.name);
        }

        // Get all cards linked to this primary card (including primary)
        const linkedCards = data.filter(w =>
          w.id === primaryCard?.id || w.primary_card_id === primaryCard?.id
        );

        // Calculate total used limit across all linked cards
        const totalUsedLimit = linkedCards.reduce((total, linkedCard) =>
          total + (linkedCard.balance < 0 ? Math.abs(linkedCard.balance) : 0), 0
        );

        // Get individual card's used amount (negative balance means used amount)
        const individualUsed = card.balance < 0 ? Math.abs(card.balance) : 0;

        // Use primary card's credit limit for all linked cards
        const creditLimit = primaryCard?.credit_limit || 0;

        // Calculate available balance and utilization
        const availableBalance = Math.max(0, creditLimit - totalUsedLimit);
        const utilization = creditLimit > 0 ? (totalUsedLimit / creditLimit) * 100 : 0;

        // Get current date info
        const today = new Date();
        const currentYear = today.getFullYear();
        const currentMonth = today.getMonth();
        const currentDay = today.getDate();

        const billingDay = parseInt(details.billing_date) || 1;
        const dueDay = parseInt(details.due_date) || (billingDay + 20);

        // Calculate current billing cycle dates
        let currentBillDate = new Date(currentYear, currentMonth, billingDay);
        let currentDueDate = new Date(currentYear, currentMonth, dueDay);

        // Adjust if we're past the due date
        if (today > currentDueDate) {
          currentBillDate = new Date(currentYear, currentMonth + 1, billingDay);
          currentDueDate = new Date(currentYear, currentMonth + 1, dueDay);
        }

        // Calculate previous billing cycle dates
        let previousBillDate = new Date(currentBillDate);
        previousBillDate.setMonth(previousBillDate.getMonth() - 1);

        // Function to get transactions for a billing cycle
        const getBillingCycleTransactions = (transactions, billDate, currentDate) => {
          try {
            // Ensure we have valid inputs
            if (!billDate || !currentDate || !transactions) {
              console.log('Invalid inputs:', { billDate, currentDate, transactionsCount: transactions?.length });
              return {
                transactions: [],
                cycleStart: null,
                cycleEnd: null
              };
            }

            // Parse the current date
            const today = new Date(currentDate);
            if (isNaN(today.getTime())) {
              console.error('Invalid current date:', currentDate);
              return {
                transactions: [],
                cycleStart: null,
                cycleEnd: null
              };
            }

            const currentYear = today.getFullYear();
            const currentMonth = today.getMonth();
            const billingDay = parseInt(billDate);

            if (isNaN(billingDay) || billingDay < 1 || billingDay > 31) {
              console.error('Invalid billing day:', billDate);
              return {
                transactions: [],
                cycleStart: null,
                cycleEnd: null
              };
            }

            // Safely create date objects
            const createSafeDate = (year, month, day) => {
              const date = new Date(year, month, day);
              // Check if the date is valid
              if (isNaN(date.getTime())) {
                console.error('Invalid date created:', { year, month, day });
                return null;
              }
              return date;
            };

            // Calculate current billing cycle dates
            let cycleStartDate = createSafeDate(currentYear, currentMonth, billingDay);
            let cycleEndDate = createSafeDate(currentYear, currentMonth + 1, billingDay);

            if (!cycleStartDate || !cycleEndDate) {
              console.error('Failed to create valid cycle dates');
              return {
                transactions: [],
                cycleStart: null,
                cycleEnd: null
              };
            }

            // If today is before the billing date, adjust to previous cycle
            if (today.getDate() < billingDay) {
              cycleStartDate = createSafeDate(currentYear, currentMonth - 1, billingDay);
              cycleEndDate = createSafeDate(currentYear, currentMonth, billingDay);
            }

            console.log('Billing Cycle Analysis:', {
              cardBillingDate: billDate,
              currentDate: today.toISOString(),
              cycleStartDate: cycleStartDate?.toISOString(),
              cycleEndDate: cycleEndDate?.toISOString()
            });

            // Filter transactions for current billing cycle
            const cycleTransactions = transactions.filter(transaction => {
              try {
                const txDate = new Date(transaction.date);
                if (isNaN(txDate.getTime())) {
                  console.error('Invalid transaction date:', transaction.date);
                  return false;
                }
                const isInCycle = txDate >= cycleStartDate && txDate < cycleEndDate;

                console.log('Transaction Analysis:', {
                  transactionDate: transaction.date,
                  amount: transaction.amount,
                  isInCurrentCycle: isInCycle
                });

                return isInCycle;
              } catch (err) {
                console.error('Error processing transaction:', err);
                return false;
              }
            });

            return {
              transactions: cycleTransactions,
              cycleStart: cycleStartDate,
              cycleEnd: cycleEndDate
            };
          } catch (err) {
            console.error('Error in getBillingCycleTransactions:', err);
            return {
              transactions: [],
              cycleStart: null,
              cycleEnd: null
            };
          }
        };

        // Calculate amounts for current and previous billing cycles
        const currentCycleTransactions = getBillingCycleTransactions(transactions, details.billing_date, today).transactions;
        const currentCycleAmount = currentCycleTransactions.reduce((sum, tx) =>
          sum + (tx.type === 'debit' ? Math.abs(tx.amount) : -Math.abs(tx.amount)), 0);

        // Get anniversary month from anniversary_date
        let anniversaryMonth = '';
        if (details.anniversary_date) {
          const [month] = details.anniversary_date.split('-');
          anniversaryMonth = month; // Keep it in "MM" format
          console.log('Anniversary date found:', {
            cardName: card.name,
            rawDate: details.anniversary_date,
            parsedMonth: anniversaryMonth
          });
        }

        // Calculate annual cycle dates and days until anniversary
        let cycleStartDate, cycleEndDate, nextAnniversaryDate, daysUntilAnniversary;

        if (anniversaryMonth && !isNaN(parseInt(anniversaryMonth))) {
          const anniversaryMonthNum = parseInt(anniversaryMonth) - 1; // Convert to 0-based month index
          cycleStartDate = new Date(currentYear, anniversaryMonthNum, 1);
          cycleEndDate = new Date(currentYear + 1, anniversaryMonthNum, 0);

          // If current date is before anniversary month, adjust cycle to previous year
          if (currentMonth < anniversaryMonthNum) {
            cycleStartDate = new Date(currentYear - 1, anniversaryMonthNum, 1);
            cycleEndDate = new Date(currentYear, anniversaryMonthNum, 0);
          }

          // Calculate days until anniversary (end of spend waiver period)
          // The next anniversary is always the 1st of the anniversary month in the next cycle
          if (currentMonth < anniversaryMonthNum || (currentMonth === anniversaryMonthNum && currentDay === 1)) {
            // Anniversary hasn't passed this year
            nextAnniversaryDate = new Date(currentYear, anniversaryMonthNum, 1);
          } else {
            // Anniversary has passed this year, so next anniversary is next year
            nextAnniversaryDate = new Date(currentYear + 1, anniversaryMonthNum, 1);
          }

          daysUntilAnniversary = Math.ceil((nextAnniversaryDate - today) / (1000 * 60 * 60 * 24));
        } else {
          // No valid anniversary month, set default values
          cycleStartDate = new Date(currentYear, 0, 1); // January 1st of current year
          cycleEndDate = new Date(currentYear + 1, 0, 0); // December 31st of current year
          nextAnniversaryDate = null;
          daysUntilAnniversary = null;
        }

        // Debug log for cycle dates
        console.log('Card Cycle Dates:', {
          cardName: card.name,
          rawAnniversaryDate: details.anniversary_date,
          parsedAnniversaryMonth: anniversaryMonth ? parseInt(anniversaryMonth) : null,
          cycleStartDate: cycleStartDate?.toISOString(),
          cycleEndDate: cycleEndDate?.toISOString(),
          nextAnniversaryDate: nextAnniversaryDate?.toISOString(),
          daysUntilAnniversary,
          currentMonth: currentMonth + 1,
          currentDay,
          currentYear
        });

        // Filter transactions for annual spend calculation
        const annualTransactions = transactions.filter(tx => {
          try {
            const txDate = new Date(tx.date);
            // Ensure valid date
            if (isNaN(txDate.getTime())) {
              console.warn('Invalid transaction date:', tx);
              return false;
            }

            // Debug log for transaction filtering
            console.log('Transaction Analysis:', {
              cardName: card.name,
              transactionId: tx.id,
              amount: tx.amount,
              type: tx.type,
              date: tx.date,
              isWithinCycle: txDate >= cycleStartDate && txDate <= cycleEndDate,
              cycleStart: cycleStartDate.toISOString(),
              cycleEnd: cycleEndDate.toISOString()
            });

            return (
              txDate >= cycleStartDate &&
              txDate <= cycleEndDate &&
              (
                tx.type === 'debit' || // Include debit transactions
                (tx.type === 'expense' && tx.amount < 0) || // Include expense transactions
                (tx.type === 'transfer' && tx.amount < 0) // Include transfers FROM the credit card for spend calculation
              )
            );
          } catch (error) {
            console.error('Error processing transaction:', error, tx);
            return false;
          }
        });

        // Calculate annual spend with detailed logging
        let annualSpend = 0;
        annualTransactions.forEach(tx => {
          const amount = Math.abs(tx.amount);
          annualSpend += amount;

          // Debug log for spend calculation
          console.log('Adding to annual spend:', {
            cardName: card.name,
            transactionId: tx.id,
            amount: amount,
            runningTotal: annualSpend
          });
        });

        // Enhanced logging for spend analysis
        console.log('Detailed Card Spend Analysis:', {
          cardName: card.name,
          anniversaryMonth: anniversaryMonth + 1,
          cycleStart: cycleStartDate.toISOString(),
          cycleEnd: cycleEndDate.toISOString(),
          totalTransactions: transactions.length,
          filteredTransactions: annualTransactions.length,
          transactionDetails: annualTransactions.map(tx => ({
            id: tx.id,
            date: tx.date,
            amount: tx.amount,
            type: tx.type
          })),
          annualSpend,
          spendWaiver: details.spend_waiver || 0,
          progress: details.spend_waiver ? (annualSpend / (details.spend_waiver || 0)) * 100 : 0,
          remaining: Math.max(0, (details.spend_waiver || 0) - annualSpend)
        });

        // Check if we need to reset initial usage
        const shouldResetInitialUsage = currentMonth === anniversaryMonth;
        const initialUsage = shouldResetInitialUsage ? 0 : (details.initial_usage || 0);

        // Calculate total spend including initial usage
        const totalAnnualSpend = annualSpend + initialUsage;

        // Calculate spend waiver progress with initial usage
        const spendWaiverAmount = details.spend_waiver || 0;
        const spendWaiverProgress = spendWaiverAmount > 0
          ? (totalAnnualSpend / spendWaiverAmount) * 100
          : 0;
        const remainingSpend = spendWaiverAmount > 0
          ? Math.max(0, spendWaiverAmount - totalAnnualSpend)
          : 0;

        // Calculate billing cycle and payment status
        let paymentStatus = 'no_bill';
        let isPastDue = false;
        let daysUntilDue = 0;
        let nextBillDate = null;
        let nextDueDate = null;
        let billAmount = 0;
        let paymentsMade = 0;
        let paymentProgress = 0;
        let isFullyPaid = false;

        if (details.billing_date && details.due_date) {
          const today = new Date();
          const currentYear = today.getFullYear();
          const currentMonth = today.getMonth();
          const currentDay = today.getDate();
          const billingDay = parseInt(details.billing_date);
          const dueDay = parseInt(details.due_date);

          // Calculate billing cycle dates more accurately
          let currentBillDate, currentDueDate, previousBillDate, billingCycleStart, billingCycleEnd;

          // Determine if we're before or after the current month's billing date
          if (currentDay >= billingDay) {
            // We're past this month's billing date, so current bill is this month
            currentBillDate = new Date(currentYear, currentMonth, billingDay);
            previousBillDate = new Date(currentYear, currentMonth - 1, billingDay);

            // Due date logic: if due day is after billing day, same month; otherwise next month
            if (dueDay >= billingDay) {
              currentDueDate = new Date(currentYear, currentMonth, dueDay);
            } else {
              currentDueDate = new Date(currentYear, currentMonth + 1, dueDay);
            }
          } else {
            // We're before this month's billing date, so current bill is last month
            currentBillDate = new Date(currentYear, currentMonth - 1, billingDay);
            previousBillDate = new Date(currentYear, currentMonth - 2, billingDay);

            // Due date logic
            if (dueDay >= billingDay) {
              currentDueDate = new Date(currentYear, currentMonth - 1, dueDay);
            } else {
              currentDueDate = new Date(currentYear, currentMonth, dueDay);
            }
          }

          // Billing cycle is STRICTLY from previous bill date (inclusive) to current bill date (exclusive)
          billingCycleStart = previousBillDate;
          billingCycleEnd = currentBillDate;

          // Get transactions for current billing cycle (STRICT: from previous bill date to current bill date)
          const billingCycleTransactions = transactions.filter(tx => {
            const txDate = new Date(tx.date);
            // Strict filtering: >= start date AND < end date
            return txDate >= billingCycleStart && txDate < billingCycleEnd;
          });

          console.log('STRICT Billing Cycle Transaction Filter:', {
            cardName: card.name,
            today: today.toISOString(),
            billingCycleStart: billingCycleStart.toISOString(),
            billingCycleEnd: billingCycleEnd.toISOString(),
            cyclePeriod: `${billingCycleStart.toLocaleDateString()} to ${billingCycleEnd.toLocaleDateString()} (exclusive)`,
            totalTransactions: transactions.length,
            filteredTransactions: billingCycleTransactions.length,
            allTransactions: transactions.map(tx => {
              const txDate = new Date(tx.date);
              const isInCycle = txDate >= billingCycleStart && txDate < billingCycleEnd;
              return {
                date: tx.date,
                amount: tx.amount,
                type: tx.type,
                description: tx.description,
                isInBillingCycle: isInCycle
              };
            })
          });

          // Calculate previous balance (all transactions before the previous bill date)
          let previousBalance = 0;
          const transactionsBeforePreviousBill = transactions.filter(tx => {
            const txDate = new Date(tx.date);
            return txDate < previousBillDate;
          });

          transactionsBeforePreviousBill.forEach(tx => {
            const amount = Math.abs(tx.amount);
            // Debits increase the outstanding balance
            if ((tx.type === 'expense' || tx.type === 'debit') && tx.amount < 0) {
              previousBalance += amount;
            } else if (tx.type === 'transfer' && tx.amount < 0) {
              previousBalance += amount;
            }
            // Credits reduce the outstanding balance
            else if ((tx.type === 'income' || tx.type === 'credit') && tx.amount > 0) {
              previousBalance -= amount;
            } else if (tx.type === 'transfer' && tx.amount > 0) {
              previousBalance -= amount;
            }
          });

          console.log('Previous Balance Calculation:', {
            cardName: card.name,
            previousBillDate: previousBillDate.toISOString(),
            transactionsBeforePreviousBill: transactionsBeforePreviousBill.length,
            previousBalance
          });

          // Calculate current cycle bill amount (sum of all expenses/debits/outgoing transfers minus credits/cashback/refunds in the cycle)
          let currentCycleBillAmount = 0;
          billingCycleTransactions.forEach(tx => {
            const amount = Math.abs(tx.amount);
            let action = 'ignored';

            // Transactions that INCREASE the bill (spending)
            if ((tx.type === 'expense' || tx.type === 'debit') && tx.amount < 0) {
              // Regular expenses and debits (negative amount means money spent)
              currentCycleBillAmount += amount;
              action = 'added (spending)';
            } else if (tx.type === 'transfer' && tx.amount < 0) {
              // Outgoing transfers FROM the credit card (negative amount means money going out)
              currentCycleBillAmount += amount;
              action = 'added (transfer out)';
            }

            // Transactions that DECREASE the bill (cashback/refunds/credits)
            else if ((tx.type === 'income' || tx.type === 'credit') && tx.amount > 0) {
              // Income and credit transactions reduce the bill amount (cashback, refunds, credits, etc.)
              currentCycleBillAmount -= amount;
              action = 'subtracted (credit/cashback/refund)';
            } else if (tx.type === 'transfer' && tx.amount > 0) {
              // Incoming transfers TO the credit card reduce the bill amount
              currentCycleBillAmount -= amount;
              action = 'subtracted (transfer in)';
            }

            console.log('Current Cycle Bill Calculation:', {
              cardName: card.name,
              txDate: tx.date,
              txType: tx.type,
              txAmount: tx.amount,
              action,
              runningTotal: currentCycleBillAmount,
              description: tx.description
            });
          });

          // Total bill amount = previous balance + current cycle amount
          billAmount = previousBalance + currentCycleBillAmount;

          console.log('Total Bill Amount Calculation:', {
            cardName: card.name,
            previousBalance,
            currentCycleBillAmount,
            totalBillAmount: billAmount
          });

          // Get payments made in the current payment cycle (from current bill date to today)
          // The previous bill (generated at current bill date) should be paid in this period
          const paymentTransactions = transactions.filter(tx => {
            const txDate = new Date(tx.date);
            return txDate >= currentBillDate &&
              txDate <= today &&
              ((tx.type === 'income' && tx.amount > 0) ||
                (tx.type === 'credit') ||
                (tx.type === 'payment') ||
                (tx.type === 'transfer' && tx.amount > 0)); // Include incoming transfers TO the credit card
          });

          // Calculate total payments made
          paymentsMade = paymentTransactions.reduce((sum, tx) => {
            return sum + Math.abs(tx.amount);
          }, 0);

          console.log('Payment Transactions:', {
            cardName: card.name,
            paymentPeriodStart: currentBillDate.toISOString(),
            paymentPeriodEnd: today.toISOString(),
            paymentTransactionsCount: paymentTransactions.length,
            paymentTransactionsList: paymentTransactions.map(tx => ({
              date: tx.date,
              amount: tx.amount,
              type: tx.type,
              description: tx.description
            })),
            totalPaymentsMade: paymentsMade
          });

          // Calculate payment progress
          paymentProgress = billAmount > 0 ? Math.min(100, (paymentsMade / billAmount) * 100) : 0;
          isFullyPaid = paymentsMade >= billAmount && billAmount > 0;

          // Calculate days until due
          daysUntilDue = Math.ceil((currentDueDate - today) / (1000 * 60 * 60 * 24));
          isPastDue = today > currentDueDate;

          // Determine payment status
          if (billAmount <= 0) {
            // Zero or positive bill amount means no payment required
            paymentStatus = 'no_payment_required';
          } else if (isFullyPaid) {
            paymentStatus = 'fully_paid';
          } else if (paymentsMade > 0) {
            paymentStatus = 'partially_paid';
          } else if (isPastDue) {
            paymentStatus = 'overdue';
          } else {
            paymentStatus = 'unpaid';
          }

          nextBillDate = currentBillDate;
          nextDueDate = currentDueDate;

          console.log('Enhanced Payment Status Calculation:', {
            cardName: card.name,
            today: today.toISOString(),
            currentDay,
            billingDay,
            dueDay,
            '--- BILLING CYCLE ---': '---',
            billingCycleStart: billingCycleStart.toISOString(),
            billingCycleEnd: billingCycleEnd.toISOString(),
            cyclePeriod: `${billingCycleStart.toLocaleDateString()} to ${billingCycleEnd.toLocaleDateString()}`,
            '--- DATES ---': '---',
            currentBillDate: currentBillDate.toISOString(),
            currentDueDate: currentDueDate.toISOString(),
            previousBillDate: previousBillDate.toISOString(),
            '--- AMOUNTS ---': '---',
            billAmount,
            paymentsMade,
            paymentProgress,
            '--- STATUS ---': '---',
            isFullyPaid,
            paymentStatus,
            isPastDue,
            daysUntilDue,
            '--- TRANSACTIONS ---': '---',
            billingCycleTransactionsCount: billingCycleTransactions.length,
            paymentTransactionsCount: paymentTransactions.length,
            billingTransactions: billingCycleTransactions.map(tx => ({
              date: tx.date,
              amount: tx.amount,
              type: tx.type,
              description: tx.description
            })),
            paymentTransactionsList: paymentTransactions.map(tx => ({
              date: tx.date,
              amount: tx.amount,
              type: tx.type,
              description: tx.description
            }))
          });
        } else {
          paymentStatus = 'no_dates_set';
        }

        return {
          ...card,
          is_archived: card.is_archived || false,
          billing_date: details.billing_date || null,
          due_date: details.due_date || null,
          anniversary_date: details.anniversary_date,
          anniversary_month: anniversaryMonth,
          annual_fee: details.annual_fee || 0,
          spend_waiver: details.spend_waiver || 0,
          rewards: details.rewards || 0,
          rewards_value: details.rewards_value || 0,
          payment_date: details.payment_date,
          paymentStatus,
          daysUntilDue,
          isPastDue,
          usedAmount: individualUsed,
          totalUsedLimit,
          availableBalance,
          utilization,
          totalLimit: creditLimit,
          rewards_points: details.rewards || 0,
          nextBillDate,
          nextDueDate,
          billAmount,
          paymentsMade,
          paymentProgress,
          isFullyPaid,
          initial_usage: initialUsage,
          annualSpend: totalAnnualSpend,
          spendWaiverProgress,
          remainingSpend,
          daysUntilAnniversary,
          cycleStartDate,
          cycleEndDate,
          isPrimary: card.is_primary,
          primaryCardId: card.primary_card_id,
          linkedCards: linkedCards.map(c => ({
            id: c.id,
            name: c.name,
            usedAmount: c.balance < 0 ? Math.abs(c.balance) : 0
          }))
        };
      });

      setCreditCards(processedCards);
      applyFilters(processedCards, searchQuery, filterType, sortBy, sortOrder);
    } catch (error) {
      console.error('Error fetching credit cards:', error);
      setError(error.message || 'Failed to fetch credit cards. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        if (isMounted) {
          await fetchCreditCards();
        }
      } catch (error) {
        if (isMounted) {
          console.error('Error in useEffect:', error);
          setError(error.message);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, []);  // Empty dependency array since fetchCreditCards uses only stable references

  // Filter and sort cards
  useEffect(() => {
    applyFilters(creditCards, searchQuery, filterType, sortBy, sortOrder);
  }, [creditCards, searchQuery, filterType, sortBy, sortOrder, archiveStatus]);

  const getLastAnniversaryDate = (anniversaryDate) => {
    const today = new Date();
    const [month, day] = anniversaryDate.split('-').map(Number);
    let year = today.getFullYear();

    const thisYearDate = new Date(year, month - 1, day);
    if (thisYearDate > today) {
      year--;
    }

    return new Date(year, month - 1, day);
  };

  const handleEditCard = (card) => {
    console.log('Editing card:', card);
    // Extract month from anniversary_date if it exists
    let anniversaryMonth = '';
    if (card.anniversary_date) {
      const [month] = card.anniversary_date.split('-');
      anniversaryMonth = month;
    }

    // Get the credit card details
    const details = card.credit_card_details?.[0] || {};

    const cardData = {
      id: card.id,
      billing_date: details.billing_date || '',
      due_date: details.due_date || '',
      anniversary_month: anniversaryMonth,
      annual_fee: details.annual_fee || 0,
      spend_waiver: details.spend_waiver || 0,
      initial_usage: details.initial_usage || 0
    };

    console.log('Setting selected card data:', cardData);
    setSelectedCard(cardData);
    setDialogOpen(true);
  };

  const handleSaveCard = async (formData) => {
    try {
      console.log('Starting save with formData:', formData);

      if (!selectedCard || !selectedCard.id) {
        throw new Error('No card selected for saving');
      }

      // First check if a record exists
      const { data: existingDetails, error: fetchError } = await supabase
        .from('credit_card_details')
        .select('*')
        .eq('wallet_id', selectedCard.id)
        .single();

      console.log('Existing details:', existingDetails);
      console.log('Fetch error:', fetchError);

      if (fetchError && fetchError.code !== 'PGRST116') {
        throw new Error(`Failed to fetch existing details: ${fetchError.message}`);
      }

      const anniversaryDate = formData.anniversary_month
        ? `${formData.anniversary_month}-01`
        : null;

      // Ensure all numeric values are properly parsed
      const cardData = {
        wallet_id: selectedCard.id,
        billing_date: formData.billing_date?.toString().padStart(2, '0'),
        due_date: formData.due_date?.toString().padStart(2, '0'),
        anniversary_date: anniversaryDate,
        annual_fee: parseFloat(formData.annual_fee) || 0,
        spend_waiver: parseFloat(formData.spend_waiver) || 0,
        initial_usage: parseFloat(formData.initial_usage) || 0,
        // Preserve existing fields if they exist
        rewards: existingDetails?.rewards || 0,
        rewards_value: existingDetails?.rewards_value || 0,
        payment_date: existingDetails?.payment_date || null
      };

      console.log('Saving card data:', cardData);

      let result;
      if (existingDetails?.id) {
        console.log('Updating existing record with ID:', existingDetails.id);
        // For existing records, use update
        result = await supabase
          .from('credit_card_details')
          .update(cardData)
          .match({ id: existingDetails.id })
          .select();
      } else {
        console.log('Creating new record');
        // For new records, use insert
        result = await supabase
          .from('credit_card_details')
          .insert(cardData)
          .select();
      }

      const { data: savedData, error: detailsError } = result;

      console.log('Save response:', { savedData, detailsError });

      if (detailsError) {
        console.error('Database error:', detailsError);
        throw new Error(`Failed to save card details: ${detailsError.message}`);
      }

      if (!savedData || savedData.length === 0) {
        throw new Error('No data returned after save');
      }

      await fetchCreditCards();
      setDialogOpen(false);
      setSelectedCard(null);

      setPaymentStatusSnackbar({
        open: true,
        message: 'Card details updated successfully!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error saving card:', error);
      setPaymentStatusSnackbar({
        open: true,
        message: error.message || 'Failed to update card details. Please try again.',
        severity: 'error'
      });
    }
  };

  const handleEditRewards = async (card) => {
    setSelectedCardRewards(card);
    await fetchRewardsHistory(card.id);
    setRewardsDialogOpen(true);
  };

  const handleViewStatements = (card) => {
    // Navigate to statements page with card data
    navigate('/statements', {
      state: {
        card: {
          id: card.id,
          name: card.name,
          billing_date: card.billing_date,
          due_date: card.due_date,
          account_number: card.account_number
        }
      }
    });
  };

  const fetchRewardsHistory = async (walletId) => {
    try {
      // Fetch rewards history from the dedicated table
      const { data, error } = await supabase
        .from('rewards_history')
        .select('*')
        .eq('wallet_id', walletId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) {
        console.error('Error fetching rewards history:', error);
        setRewardsHistory([]);
      } else {
        setRewardsHistory(data || []);
      }
    } catch (error) {
      console.error('Error fetching rewards history:', error);
      setRewardsHistory([]);
    }
  };

  const handleSaveRewards = async (formData) => {
    try {
      // First check if a record exists
      const { data: existingDetails, error: fetchError } = await supabase
        .from('credit_card_details')
        .select('*')
        .eq('wallet_id', selectedCardRewards.id)
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') {
        throw fetchError;
      }

      // Format rewards_value to ensure it meets numeric(5,4) constraints
      const rewardsValue = parseFloat(formData.rewards_value || 0);
      const safeRewardsValue = Number(Math.min(Math.max(0, rewardsValue), 0.9999).toFixed(4));

      // Update or insert rewards details
      const { error: detailsError } = await supabase
        .from('credit_card_details')
        .upsert({
          ...(existingDetails?.id ? { id: existingDetails.id } : {}),
          wallet_id: selectedCardRewards.id,
          rewards: existingDetails?.rewards || 0, // Preserve existing rewards points
          rewards_value: safeRewardsValue,
          // Preserve other fields if they exist
          billing_date: existingDetails?.billing_date || null,
          due_date: existingDetails?.due_date || null,
          anniversary_date: existingDetails?.anniversary_date || null,
          annual_fee: existingDetails?.annual_fee || 0,
          spend_waiver: existingDetails?.spend_waiver || 0
        });

      if (detailsError) throw detailsError;

      await fetchCreditCards();
      setRewardsDialogOpen(false);
      setSelectedCardRewards(null);
    } catch (error) {
      console.error('Error saving rewards:', error);
      alert(error.message);
    }
  };

  const handleUpdateRewardsPoints = async () => {
    try {
      if (!rewardsPointsInput || !selectedCardRewards) return;
      setLoading(true);

      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError || !user) {
        throw new Error('User authentication failed');
      }

      // First check if a record exists
      const { data: existingDetails, error: fetchError } = await supabase
        .from('credit_card_details')
        .select('*')
        .eq('wallet_id', selectedCardRewards.id)
        .single();

      if (fetchError && fetchError.code !== 'PGRST116') {
        console.error('Fetch error:', fetchError);
        throw new Error('Failed to fetch existing rewards details');
      }

      // Convert to numbers and validate
      const currentPoints = parseInt(existingDetails?.rewards || 0);
      const pointsToAdd = parseInt(rewardsPointsInput);

      // Calculate new points value, ensure it's within safe bigint limits
      const MAX_SAFE_POINTS = Number.MAX_SAFE_INTEGER; // JavaScript's safe integer limit
      const newPoints = Math.max(0, Math.min(MAX_SAFE_POINTS, currentPoints + pointsToAdd));

      // Prepare update data
      const updateData = {
        wallet_id: selectedCardRewards.id,
        rewards: newPoints
      };

      // If existing record found, include its ID and preserve other fields
      if (existingDetails?.id) {
        updateData.id = existingDetails.id;
        updateData.rewards_value = existingDetails.rewards_value || 0;
        updateData.billing_date = existingDetails.billing_date;
        updateData.due_date = existingDetails.due_date;
        updateData.anniversary_date = existingDetails.anniversary_date;
        updateData.annual_fee = existingDetails.annual_fee || 0;
        updateData.spend_waiver = existingDetails.spend_waiver || 0;
      }

      // Update or insert rewards details
      const { data: updatedData, error: detailsError } = await supabase
        .from('credit_card_details')
        .upsert(updateData)
        .select()
        .single();

      if (detailsError) {
        console.error('Database error details:', detailsError);
        throw new Error(`Failed to update rewards points: ${detailsError.message}`);
      }

      if (!updatedData) {
        throw new Error('No data returned after update');
      }

      // Create rewards history entry
      const historyEntry = {
        wallet_id: selectedCardRewards.id,
        user_id: user.id,
        points_change: pointsToAdd,
        points_before: currentPoints,
        points_after: newPoints,
        description: pointsToAdd > 0
          ? `Added ${pointsToAdd.toLocaleString()} reward points`
          : `Subtracted ${Math.abs(pointsToAdd).toLocaleString()} reward points`,
        transaction_type: 'manual_update'
      };

      const { error: historyError } = await supabase
        .from('rewards_history')
        .insert(historyEntry);

      if (historyError) {
        console.error('Error saving rewards history:', historyError);
        // Don't throw error here, just log it as the main update succeeded
      }

      // Update local state
      setSelectedCardRewards({
        ...selectedCardRewards,
        rewards_points: newPoints,
        credit_card_details: {
          ...selectedCardRewards.credit_card_details,
          ...updatedData
        }
      });

      // Clear input and refresh history
      setRewardsPointsInput('');
      await fetchRewardsHistory(selectedCardRewards.id);

      // Refresh cards
      await fetchCreditCards();

      // Show success message in snackbar
      setPaymentStatusSnackbar({
        open: true,
        message: `Successfully ${pointsToAdd >= 0 ? 'added' : 'subtracted'} ${Math.abs(pointsToAdd)} points. New total: ${newPoints.toLocaleString()}`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating rewards points:', error);
      setPaymentStatusSnackbar({
        open: true,
        message: error.message || 'Failed to update rewards points. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Apply filters, search, and sorting
  const applyFilters = (cards, query, filter, sort, order) => {
    let result = [...cards];

    // Apply archive status filter first
    if (archiveStatus === 'active') {
      result = result.filter(card => !card.is_archived);
    } else if (archiveStatus === 'archived') {
      result = result.filter(card => card.is_archived);
    }
    // 'all' status doesn't need filtering

    // Apply search query
    if (query) {
      result = result.filter(card =>
        card.name.toLowerCase().includes(query.toLowerCase())
      );
    }

    // Apply filter type
    if (filter === 'used') {
      result = result.filter(card => card.usedAmount > 0);
    } else if (filter === 'unused') {
      result = result.filter(card => card.usedAmount === 0);
    } else if (STATUS_GROUPS[filter]) {
      result = result.filter(card => STATUS_GROUPS[filter].includes(card.paymentStatus));
    }

    // Map sortBy to actual property names or computed properties
    const sortFieldMap = {
      name: 'name',
      credit_limit: 'totalLimit',
      available_limit: 'availableBalance',
      bill_date: 'billing_date',
      spend_waiver: 'spend_waiver',
      fee_waiver_progress: 'spendWaiverProgress',
      anniversary_month: 'anniversary_month',
      annual_fee: 'annual_fee',
    };
    const sortKey = sortFieldMap[sort] || sort;

    result.sort((a, b) => {
      let valueA = a[sortKey];
      let valueB = b[sortKey];

      // Handle types for special fields
      if (sortKey === 'name') {
        valueA = (valueA || '').toLowerCase();
        valueB = (valueB || '').toLowerCase();
      } else if (sortKey === 'billing_date' || sortKey === 'anniversary_month') {
        // Sort as numbers (month/day)
        valueA = Number(valueA) || 0;
        valueB = Number(valueB) || 0;
      } else {
        valueA = typeof valueA === 'number' ? valueA : Number(valueA) || 0;
        valueB = typeof valueB === 'number' ? valueB : Number(valueB) || 0;
      }

      if (valueA < valueB) return order === 'asc' ? -1 : 1;
      if (valueA > valueB) return order === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredCards(result);
  };



  // Handle close snackbar
  const handleCloseSnackbar = () => {
    setPaymentStatusSnackbar({
      ...paymentStatusSnackbar,
      open: false
    });
  };

  // Add handleArchive function
  const handleArchive = async (card) => {
    try {
      const { error } = await supabase
        .from('wallets')
        .update({ is_archived: !card.is_archived })
        .eq('id', card.id);

      if (error) throw error;
      await fetchCreditCards();
    } catch (error) {
      console.error('Error archiving card:', error);
      setPaymentStatusSnackbar({
        open: true,
        message: 'Failed to archive card. Please try again.',
        severity: 'error'
      });
    }
  };

  // Update the archive status change handler
  const handleArchiveStatusChange = (e) => {
    setArchiveStatus(e.target.value);
    applyFilters(creditCards, searchQuery, filterType, sortBy, sortOrder);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Error: {error}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Box sx={{}}>
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: 'text.primary' }}>
              Credit Cards
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Limits, bills and rewards across your cards
            </Typography>
          </Box>
          <TourButton onClick={cardsTour.start} />
        </Box>

        <Box data-tour="cc-summary">
          <CreditCardsSummary cards={filteredCards} />
        </Box>

        {/* Search, Filter and Sort Controls */}
        <Paper data-tour="cc-filters" sx={{ p: { xs: 1.5, sm: 2 }, mb: 3 }}>
          <Grid container spacing={{ xs: 1.5, sm: 2 }} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Search Cards"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  applyFilters(creditCards, e.target.value, filterType, sortBy, sortOrder);
                }}
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Filter</InputLabel>
                <Select
                  value={filterType}
                  label="Filter"
                  onChange={(e) => {
                    const value = e.target.value;
                    setFilterType(value);
                    applyFilters(creditCards, searchQuery, value, sortBy, sortOrder);
                    // Keep the address in step so Back / refresh keep the filter
                    const next = new URLSearchParams(searchParams);
                    if (value === 'all') next.delete('filter'); else next.set('filter', value);
                    setSearchParams(next, { replace: true });
                  }}
                >
                  {FILTER_OPTIONS.map(option => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort By</InputLabel>
                <Select
                  value={sortBy}
                  label="Sort By"
                  onChange={(e) => {
                    setSortBy(e.target.value);
                    applyFilters(creditCards, searchQuery, filterType, e.target.value, sortOrder);
                  }}
                >
                  {sortOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Archive Status</InputLabel>
                <Select
                  value={archiveStatus}
                  label="Archive Status"
                  onChange={handleArchiveStatusChange}
                >
                  {archiveStatusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton
                  onClick={() => {
                    const newOrder = sortOrder === 'asc' ? 'desc' : 'asc';
                    setSortOrder(newOrder);
                    applyFilters(creditCards, searchQuery, filterType, sortBy, newOrder);
                  }}
                  size="small"
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }}
                >
                  {sortOrder === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
                </IconButton>
                <Typography variant="body2" color="text.secondary">
                  {sortOrder === 'asc' ? 'Ascending' : 'Descending'}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        <Grid container spacing={{ xs: 2, md: 2.5 }}>
          {loading ? (
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
              <CircularProgress />
            </Grid>
          ) : error ? (
            <Grid item xs={12}>
              <Alert severity="error">{error}</Alert>
            </Grid>
          ) : filteredCards.length === 0 ? (
            <Grid item xs={12}>
              <Alert severity="info">No credit cards found.</Alert>
            </Grid>
          ) : (
            <>
              {(rowsPerPage > 0
                ? filteredCards.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                : filteredCards
              ).map((card) => (
                <Grid item xs={12} key={card.id}>
                  <CreditCardItem
                    card={card}
                    getMonthName={getMonthName}
                    onEdit={handleEditCard}
                    onArchive={handleArchive}
                    onRewards={handleEditRewards}
                    onStatements={handleViewStatements}
                  />
                </Grid>
              ))}

              {/* Pagination */}
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                  <Stack spacing={2} alignItems="center">
                    <Box sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2
                    }}>
                      <Typography variant="body2" color="text.secondary">
                        Rows per page:
                      </Typography>
                      <Select
                        value={rowsPerPage}
                        onChange={(e) => {
                          setRowsPerPage(e.target.value);
                          setPage(0); // Reset to first page when changing rows per page
                        }}
                        size="small"
                        sx={{ minWidth: 80 }}
                      >
                        {[5, 10, 20, 30].map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                      <Typography variant="body2" color="text.secondary">
                        {page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, filteredCards.length)} of {filteredCards.length} cards
                      </Typography>
                    </Box>
                    <Pagination
                      count={Math.ceil(filteredCards.length / rowsPerPage)}
                      page={page + 1}
                      onChange={(event, newPage) => setPage(newPage - 1)}
                      color="primary"
                      showFirstButton
                      showLastButton
                    />
                  </Stack>
                </Box>
              </Grid>
            </>
          )}
        </Grid>

        <Dialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Edit Card Details{selectedCard?.name ? ` - ${selectedCard.name}` : ''}
          </DialogTitle>
          <DialogContent>
            <Alert severity="info" sx={{ mt: 1 }}>
              <b>Billing Date</b> and <b>Due Date</b> let Mickles work out each month's bill and remind you when it's due.
              The fee fields track how close you are to getting this year's annual fee waived.
            </Alert>
            <Box component="form" sx={{ mt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Billing Date"
                    type="number"
                    fullWidth
                    placeholder="DD"
                    value={selectedCard?.billing_date ?? ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || (parseInt(value) >= 1 && parseInt(value) <= 31)) {
                        setSelectedCard({
                          ...selectedCard,
                          billing_date: value
                        });
                      }
                    }}
                    inputProps={{
                      min: 1,
                      max: 31
                    }}
                    helperText="Day your statement is generated each month (1-31)"
                    sx={numberInputStyle}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Due Date"
                    type="text"
                    fullWidth
                    placeholder="DD"
                    value={selectedCard?.due_date ?? ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === '' || (parseInt(value) >= 1 && parseInt(value) <= 31)) {
                        setSelectedCard({
                          ...selectedCard,
                          due_date: value
                        });
                      }
                    }}
                    helperText="Day payment is due (1-31). If it falls in the next month, just enter the day, e.g. 3"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Anniversary Month"
                    fullWidth
                    value={selectedCard?.anniversary_month ?? ''}
                    onChange={(e) => {
                      setSelectedCard({
                        ...selectedCard,
                        anniversary_month: e.target.value
                      });
                    }}
                    helperText="Month your card year starts; the fee and spend waiver reset then"
                  >
                    {months.map((month) => (
                      <MenuItem key={month.value} value={month.value}>
                        {month.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <AmountField
                    label="Annual Fee"
                    type="number"
                    fullWidth
                    value={selectedCard?.annual_fee ?? ''}
                    onChange={(e) => {
                      setSelectedCard({
                        ...selectedCard,
                        annual_fee: parseFloat(e.target.value)
                      });
                    }}
                    InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                    helperText="Yearly fee charged for this card (0 if free)"
                    sx={numberInputStyle}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <AmountField
                    label="Spend Waiver"
                    type="number"
                    fullWidth
                    value={selectedCard?.spend_waiver ?? ''}
                    onChange={(e) => {
                      setSelectedCard({
                        ...selectedCard,
                        spend_waiver: parseFloat(e.target.value)
                      });
                    }}
                    InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                    helperText="Spend needed in a card year to get the annual fee waived (0 if none)"
                    sx={numberInputStyle}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <AmountField
                    label="Initial Usage Amount"
                    type="number"
                    fullWidth
                    value={selectedCard?.initial_usage ?? ''}
                    onChange={(e) => {
                      setSelectedCard({
                        ...selectedCard,
                        initial_usage: parseFloat(e.target.value) || 0
                      });
                    }}
                    InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                    helperText="Spend already made this card year before you started tracking it here"
                    sx={numberInputStyle}
                  />
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => handleSaveCard(selectedCard)}
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={rewardsDialogOpen}
          onClose={() => setRewardsDialogOpen(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Rewards Management - {selectedCardRewards?.name}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Keep this card's reward points up to date. Enter a <b>positive</b> number to add points you've earned
                (e.g. 500) or a <b>negative</b> number to subtract points you've redeemed (e.g. -2000), then press <b>Update</b>.
                Set what 1 point is worth to see their value in rupees. Every change is recorded in the history below.
              </Alert>
              {/* Update Rewards Section - Moved to Top */}
              <Paper sx={{ p: 3, mb: 3, bgcolor: '#f8f9fa' }}>
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                  Update Rewards Points
                </Typography>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" gutterBottom>
                      Current Rewards Points: {selectedCardRewards?.rewards_points?.toLocaleString() || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Current Value: {formatCurrency((selectedCardRewards?.rewards_points || 0) * (selectedCardRewards?.rewards_value || 0))}
                    </Typography>
                  </Grid>

                  {/* Add/Subtract Points Section */}
                  <Grid item xs={12}>
                    <Box sx={{
                      display: 'flex',
                      gap: 1,
                      alignItems: 'flex-start'
                    }}>
                      <TextField
                        label="Add/Subtract Points"
                        type="number"
                        value={rewardsPointsInput ?? ''}
                        onChange={(e) => setRewardsPointsInput(e.target.value)}
                        helperText="e.g. 500 to add, -2000 to subtract"
                        fullWidth
                        sx={[{ flex: 1 }, numberInputStyle]}
                      />
                      <Button
                        variant="contained"
                        onClick={handleUpdateRewardsPoints}
                        disabled={!rewardsPointsInput || rewardsPointsInput === '0'}
                        sx={{
                          minWidth: '100px',
                          height: '56px',
                          mt: '0px',
                          backgroundColor: '#4caf50',
                          '&:hover': {
                            backgroundColor: '#388e3c'
                          }
                        }}
                      >
                        Update
                      </Button>
                    </Box>
                  </Grid>

                  {/* Value per Point Setting */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="1 Reward Point = ₹"
                      type="number"
                      fullWidth
                      inputProps={{
                        step: "0.0001",
                        min: "0"
                      }}
                      value={selectedCardRewards?.rewards_value ?? ''}
                      onChange={(e) => {
                        const value = Math.max(0, Number(parseFloat(e.target.value || 0).toFixed(4)));
                        setSelectedCardRewards({
                          ...selectedCardRewards,
                          rewards_value: value
                        });
                      }}
                      helperText="Check your card's rewards terms, e.g. 0.25 if 4 points = ₹1"
                      sx={numberInputStyle}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Button
                      onClick={() => handleSaveRewards(selectedCardRewards)}
                      variant="outlined"
                      fullWidth
                      sx={{ height: '56px' }}
                    >
                      Save Point Value
                    </Button>
                  </Grid>
                </Grid>
              </Paper>

              {/* Rewards History Section */}
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ color: 'primary.main' }}>
                  Rewards History
                </Typography>

                {rewardsHistory.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    No rewards history found. Rewards transactions will appear here.
                  </Typography>
                ) : (
                  <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                    {rewardsHistory.map((transaction, index) => (
                      <Box
                        key={transaction.id}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: 2,
                          borderBottom: index < rewardsHistory.length - 1 ? '1px solid #e0e0e0' : 'none'
                        }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body2" fontWeight="medium">
                            {transaction.description || 'Rewards Transaction'}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(transaction.created_at)} • {transaction.transaction_type}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                            {transaction.points_before.toLocaleString()} → {transaction.points_after.toLocaleString()} points
                          </Typography>
                        </Box>
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography
                            variant="body2"
                            fontWeight="medium"
                            sx={{
                              color: transaction.points_change > 0 ? 'success.main' : 'error.main'
                            }}
                          >
                            {transaction.points_change > 0 ? '+' : ''}{transaction.points_change.toLocaleString()} pts
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatCurrency(Math.abs(transaction.points_change) * (selectedCardRewards?.rewards_value || 0))}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                )}
              </Paper>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRewardsDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Add Snackbar for payment status updates */}
        <Snackbar
          open={paymentStatusSnackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          action={paymentStatusSnackbar.action}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={paymentStatusSnackbar.severity}
            sx={{ width: '100%' }}
          >
            {paymentStatusSnackbar.message}
          </Alert>
        </Snackbar>
      </Box>

      <GuidedTour steps={TOURS['credit-cards']} open={cardsTour.open} onClose={cardsTour.close} />
    </Box>
  );
};

export default CreditCards;
